import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CustomPagination from '../../../components/forms/Pagination';
import { useTranslation } from 'react-i18next';
import { Link, TableSortLabel, Tooltip, Typography, useTheme, ClickAwayListener } from '@mui/material';
import { GetStockResponse, StockItem } from '../../../app/services/api/generated';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import RoundAndFormatValue from '../../../components/shared/RoundAndFormatValue';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import { TableLoading } from '../../../components/forms/TableLoading';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { Info } from '@mui/icons-material';

const StockOnHandTable = (props: {
  readonly list: GetStockResponse;
  readonly setPageZero: number;
  readonly loadData: (sort: string, direction: string, skip: number, take: number) => void;
  readonly clearAll: () => void;
  readonly isLoading: boolean;
  readonly isOrderableQuantity: boolean;
  readonly lastSearch: string;
}) => {
  const theme = useTheme();

  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const [page, setPage] = React.useState(0);
  const rowsPerPage = 10;

  const maxPages = props.list?.totalCount ? Math.ceil(props.list.totalCount / 10) : 0;

  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = React.useState<string>('itemNumber');
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  useEffect(() => {
    props.loadData(orderBy, order, page * rowsPerPage, rowsPerPage);
  }, [page]);

  useEffect(() => {
    setPage(0);
  }, [props.setPageZero]);

  const isAsc = order === 'asc';

  const handleSort = (property: string) => {
    const order = isAsc ? 'desc' : 'asc';
    setOrder(order);
    setOrderBy(property);
    props.loadData(property, order, page * rowsPerPage, rowsPerPage);
  };

  const getFormattedQuantity = (row: StockItem): string => {
    if (props.isOrderableQuantity && !row.isQuantityConverted) {
      return 'N/A';
    }

    const quantity = props.isOrderableQuantity ? row.quantity : row.quantityInEaches;
    return quantity != null ? Number(quantity).toFixed(2) : '';
  };

  const handleTooltipToggle = (event: React.MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    setTooltipOpen(!tooltipOpen);
  };

  if (props.list?.totalCount == 0) {
    return <EmptyTable text={'Stock Items'}
      searchTerm={props.lastSearch}
      onClear={props.clearAll}
      onAdd={() => { }}
      showOnAdd={false}
      addItemText={''}
      onAddIsCreating={false}
      headerHeight={45}
      bodyRowHeight={40} />;
  }

  return (
    <>
      <TableHeader>
        {t('itemsCapialised')}
        {(props.list?.totalCount ?? 0) > 0 && <Pill text={`${props.list?.totalCount} ${t('itemsCapialised')}`}></Pill>}
      </TableHeader>
      {props.isLoading ? <TableLoading headerHeight={45}
        bodyRowHeight={40}
        minTableWidth={850} /> :
        <TableContainer>
          <Table sx={{ minWidth: '850px' }}>
            <TableHead>
              <TableRow>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <TableSortLabel
                    active={orderBy === 'itemNumber'}
                    direction={order}
                    onClick={() => handleSort('itemNumber')}
                  >
                    {t('stockOnHand.sku')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <TableSortLabel
                    active={orderBy === 'description'}
                    direction={order}
                    onClick={() => handleSort('description')}
                  >
                    {t('stockOnHand.name')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <TableSortLabel
                    active={orderBy === 'primaryCategory'}
                    direction={order}
                    onClick={() => handleSort('primaryCategory')}
                  >
                    {t('stockOnHand.category')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <TableSortLabel
                    active={orderBy === 'quantity'}
                    direction={order}
                    onClick={() => handleSort('quantity')}
                  >
                    {t('stockOnHand.qty')}
                    <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
                      <Tooltip
                        title={t('stockOnHand.infoTooltip')}
                        open={tooltipOpen}
                        onClose={() => setTooltipOpen(false)}
                        disableHoverListener
                        arrow
                        componentsProps={{
                          tooltip: {
                            onClick: (event: React.MouseEvent) => {
                              event.stopPropagation();
                            },
                          },
                        }}
                      >
                        <Info
                          sx={{ ml: 1, fontSize: 16, cursor: 'pointer' }}
                          onClick={handleTooltipToggle}
                        />
                      </Tooltip>
                    </ClickAwayListener>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <TableSortLabel
                    active={orderBy === 'costPrice'}
                    direction={order}
                    onClick={() => handleSort('costPrice')}
                  >
                    {t('stockOnHand.costPrice')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <TableSortLabel
                    active={orderBy === 'totalCost'}
                    direction={order}
                    onClick={() => handleSort('totalCost')}
                  >
                    {t('stockOnHand.totalCost')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {t('store')}
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <TableSortLabel
                    active={orderBy === 'minimumQuantity'}
                    direction={order}
                    onClick={() => handleSort('minimumQuantity')}
                  >
                    {t('stockOnHand.minQty')}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.list?.stock?.map((row) => (
                <TableRow key={row.itemNumber}
                  sx={{ minHeight: '40px', height: '40px', cursor: 'pointer' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/store/item/${row.storeNumber}?sku=${row.itemNumber}`);
                  }}>
                  <TableCell>
                    <Typography variant='textXS'
                      fontWeight={600}
                      color={theme.palette.primary[500]}
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {row.itemNumber}
                    </Typography>
                  </TableCell>
                  <TableCell variant='clampedText'>
                    <Typography variant='textXS'>
                      {row.description}
                    </Typography>
                  </TableCell>
                  <TableCell variant='clampedText'>
                    <Typography variant='textXS'>
                      {row.primaryCategory}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t('stockOnHand.viewTransactions')}
                      arrow>
                      <Link
                        href={`/transactions/${row.itemNumber}`}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                        color="primary"
                        underline="hover"
                        style={{ cursor: 'pointer' }}
                      >
                        {getFormattedQuantity(row)}
                      </Link>
                    </Tooltip>
                    (<Tooltip title={t('stockOnHand.viewUOM')}
                      arrow>
                      <Link
                        href={`/uom-conversions/${row.itemNumber}`}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                        color="primary"
                        underline="hover"
                        style={{ cursor: 'pointer' }}
                      >
                        {props.isOrderableQuantity ? row.orderableUom : row.uom}
                      </Link>
                    </Tooltip>)
                  </TableCell>
                  <TableCell>
                    <RoundAndFormatValue
                      value={row?.costPrice ?? 0}
                      currencyCode={currencyCode}
                    />
                  </TableCell>
                  <TableCell>
                    <RoundAndFormatValue
                      value={row?.totalCost ?? 0}
                      currencyCode={currencyCode}
                    />
                  </TableCell>
                  <TableCell>
                    {row.starbucksStoreNumber}
                  </TableCell>
                  <TableCell>
                    {row.minimumQuantity !== undefined ? Number(row.minimumQuantity).toFixed(2) : ''}
                  </TableCell>
                </TableRow>
              ))}

              <EmptyRows numberOfCurrentRows={props.list.stock?.length ?? 0}
                height={40} />
            </TableBody>
          </Table>
        </TableContainer>
      }

      <CustomPagination useNoDataMessage={false}
        page={page}
        setPage={setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
      />
    </>
  );
};

export default StockOnHandTable;
